/**
 * WHAT: Main reports dashboard and router for all DAMA reporting views.
 * WHY:  Provides a centralized entry point for database access reports, privilege change
 *       tracking, and campaign attestation summaries across all supported database technologies,
 *       enabling compliance teams to monitor and audit user access from one place.
 */
import React, { useState, useEffect, Fragment } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { formatDateTime } from '../utils/dateUtils';

import API_CONFIG from '../config/api.config';

/**
 * WHAT: Renders the reports dashboard with navigation between sub-reports and inline report views.
 * WHY:  Compliance and DBA teams need a single page to switch between privilege summaries,
 *       privilege change timelines, and attestation campaign statistics without losing filter context.
 */
const Reports = () => {
  const [activeReport, setActiveReport] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [filters, setFilters] = useState({
    databaseType: 'all',
    databaseName: 'all'
  });
  const [databaseTypes, setDatabaseTypes] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [databaseNames, setDatabaseNames] = useState([]);

  // State for User Privileges Report
  const [selectedDatabase, setSelectedDatabase] = useState('all');
  const [selectedUsername, setSelectedUsername] = useState('');
  const [privilegeLevel, setPrivilegeLevel] = useState('all');

  // State for Privilege Changes Report
  const [fromDate, setFromDate] = useState(''); // Will be set from first available collection
  const [toDate, setToDate] = useState(''); // Will be set from last available collection (today)
  const [changeType, setChangeType] = useState('all');
  const [privilegeChangesData, setPrivilegeChangesData] = useState({
    statistics: {
      databases_tracked: 0,
      total_collections: 0,
      privileges_granted: 0,
      privileges_revoked: 0,
      users_affected: 0,
      net_change: 0
    },
    timeline: [],
    recent_changes: []
  });

  // State for table sorting, filtering, and pagination
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [tableFilters, setTableFilters] = useState({
    username: '',
    database: '',
    changeType: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for change details modal
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);

  // Fetch dropdown options on component mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Fetch report data based on active report
  useEffect(() => {
    if (activeReport !== 'dashboard') {
      fetchReportData();
    }
  }, [activeReport, filters]);

  // Update database names when database type filter changes
  useEffect(() => {
    if (allConnections.length > 0) {
      if (filters.databaseType === 'all') {
        // Show all database names
        const uniqueDbNames = [...new Set(allConnections.map(conn => conn.database_name))];
        setDatabaseNames(uniqueDbNames.sort());
      } else {
        // Filter database names by selected database type
        const filteredConnections = allConnections.filter(conn => conn.database_type === filters.databaseType);
        const uniqueDbNames = [...new Set(filteredConnections.map(conn => conn.database_name))];
        setDatabaseNames(uniqueDbNames.sort());
      }
      // Reset database name to 'all' when database type changes
      setFilters(prev => ({ ...prev, databaseName: 'all' }));
    }
  }, [filters.databaseType, allConnections]);

  // Clear dates when switching between Oracle and MySQL reports
  useEffect(() => {
    if (activeReport === 'privilege-changes-oracle' || activeReport === 'privilege-changes-mysql') {
      setFromDate('');
      setToDate('');
    }
  }, [activeReport]);

  // Fetch privilege changes data when relevant dependencies change
  useEffect(() => {
    if (activeReport === 'privilege-changes-oracle' || activeReport === 'privilege-changes-mysql') {
      // Clear previous data immediately when switching between Oracle and MySQL reports
      setPrivilegeChangesData({
        statistics: {},
        timeline: [],
        recent_changes: []
      });
      fetchPrivilegeChangesData();
    }
  }, [activeReport, fromDate, toDate, filters]);

  const fetchFilterOptions = async () => {
    try {
      // Fetch database types
      const dbTypesResponse = await fetch(API_CONFIG.getEndpoint('/api/databases/config/database-types/all/'));
      const dbTypesData = await dbTypesResponse.json();
      if (dbTypesData.success) {
        setDatabaseTypes(dbTypesData.database_types || []);
      }

      // Fetch database connections
      const connectionsResponse = await fetch(API_CONFIG.getEndpoint('/api/databases/connections/'));
      const connectionsData = await connectionsResponse.json();
      if (connectionsData.success) {
        // Store all connections for filtering
        setAllConnections(connectionsData.connections || []);
        // Initially show all database names
        const uniqueDbNames = [...new Set(connectionsData.connections.map(conn => conn.database_name))];
        setDatabaseNames(uniqueDbNames.sort());
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/reports/${activeReport}/?${queryParams}'));
      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivilegeChangesData = async () => {
    setLoading(true);
    try {
      // Determine database_type based on active report
      let databaseType = 'all';
      if (activeReport === 'privilege-changes-oracle') {
        databaseType = 'oracle';
      } else if (activeReport === 'privilege-changes-mysql') {
        databaseType = 'mysql';
      }

      const params = new URLSearchParams({
        from_date: fromDate || '',  // Empty string will make backend use first collection
        to_date: toDate || '',      // Empty string will make backend use last collection
        database_type: databaseType,
        database_name: filters.databaseName
      });
      const response = await fetch(API_CONFIG.getEndpoint(`/api/databases/reports/privilege-changes/?${params}`));
      const data = await response.json();
      if (data.success) {
        setPrivilegeChangesData({
          statistics: data.statistics || {},
          timeline: data.timeline || [],
          recent_changes: data.recent_changes || []
        });

        // Update date inputs with actual dates used by the backend if they were empty
        if (data.query_parameters) {
          if (!fromDate && data.query_parameters.from_date) {
            setFromDate(data.query_parameters.from_date);
          }
          if (!toDate && data.query_parameters.to_date) {
            setToDate(data.query_parameters.to_date);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching privilege changes data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const queryParams = new URLSearchParams({...filters, format}).toString();
      const response = await fetch(API_CONFIG.getEndpoint('/api/databases/reports/${activeReport}/export/?${queryParams}'));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReport}_report_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    }
  };

  const renderDashboard = () => (
    <div>
      {/* Report Types Grid */}
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="mb-3">Available Reports</h4>
        </div>
        <div className="col-md-6 col-lg-4 mb-3">
          <div
            className="card shadow-sm border-0 h-100 hover-shadow"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveReport('attestation')}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                  <i className="fas fa-tasks fa-2x text-success"></i>
                </div>
                <h5 className="mb-0">Attestation Status</h5>
              </div>
              <p className="text-muted mb-0">
                Monitor attestation progress, attester performance, and overdue items. Real-time tracking of all attestation activities.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 mb-3">
          <div
            className="card shadow-sm border-0 h-100 hover-shadow"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveReport('user-privileges')}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle bg-danger bg-opacity-10 p-3 me-3">
                  <i className="fas fa-key fa-2x text-danger"></i>
                </div>
                <h5 className="mb-0">User Privileges</h5>
              </div>
              <p className="text-muted mb-0">
                Comprehensive view of Oracle user privileges across databases. View DBA, table, role, and system privileges by framework.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 mb-3">
          <div
            className="card shadow-sm border-0 h-100 hover-shadow"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveReport('privilege-changes-oracle')}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                  <i className="fas fa-history fa-2x text-warning"></i>
                </div>
                <h5 className="mb-0">Privilege Changes - Oracle</h5>
              </div>
              <p className="text-muted mb-0">
                Track Oracle privilege grants and revocations over time. Identify privilege drift, escalations, and anomalies.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 mb-3">
          <div
            className="card shadow-sm border-0 h-100 hover-shadow"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveReport('privilege-changes-mysql')}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                  <i className="fas fa-clock fa-2x text-info"></i>
                </div>
                <h5 className="mb-0">Privilege Changes - MySQL</h5>
              </div>
              <p className="text-muted mb-0">
                Track MySQL privilege grants and revocations over time. Monitor global, database, and table-level privilege changes.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-4 mb-3">
          <div
            className="card shadow-sm border-0 h-100 hover-shadow"
            style={{ cursor: 'pointer' }}
            onClick={() => setActiveReport('privilege-audit')}
          >
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                  <i className="fas fa-clipboard-check fa-2x text-info"></i>
                </div>
                <h5 className="mb-0">Privilege Auditing</h5>
              </div>
              <p className="text-muted mb-0">
                Audit critical privileges, detect over-privileged users, and ensure compliance. Identify SYSDBA and ADMIN privileges.
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .hover-shadow {
            transition: all 0.3s ease;
          }
          .hover-shadow:hover {
            transform: translateY(-5px);
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
          }
        `}
      </style>
    </div>
  );

  const renderFilters = () => {
    return (
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label small">Database Type</label>
              <select
                className="form-select form-select-sm"
                value={filters.databaseType}
                onChange={(e) => setFilters({...filters, databaseType: e.target.value})}
              >
                <option value="all">All Types</option>
                {databaseTypes
                  .filter(dbType => dbType.is_active === true)
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map((dbType, index) => (
                    <option key={index} value={dbType.type_name}>
                      {dbType.type_name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small">Database Name</label>
              <select
                className="form-select form-select-sm"
                value={filters.databaseName}
                onChange={(e) => setFilters({...filters, databaseName: e.target.value})}
              >
                <option value="all">All Databases</option>
                {databaseNames.map((dbName, index) => (
                  <option key={index} value={dbName}>
                    {dbName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <button
                className="btn btn-primary btn-sm w-100"
                onClick={fetchReportData}
              >
                <i className="fas fa-search me-1"></i> Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AttestationReportContent = () => {
    const summary = reportData.summary || {};
    const attesters = reportData.attesters || [];
    const [expandedRows, setExpandedRows] = useState([]);

    const toggleRow = (index) => {
      if (expandedRows.includes(index)) {
        setExpandedRows(expandedRows.filter(i => i !== index));
      } else {
        setExpandedRows([...expandedRows, index]);
      }
    };

    return (
      <div>
        {renderFilters()}

        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h3 className="text-primary mb-2">{summary.total_attestations || 0}</h3>
                <p className="text-muted mb-0">Total Attestations</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h3 className="text-success mb-2">{summary.approved || 0}</h3>
                <p className="text-muted mb-0">Approved</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h3 className="text-danger mb-2">{summary.revoked || 0}</h3>
                <p className="text-muted mb-0">Revoked</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <h3 className="text-warning mb-2">{summary.pending || 0}</h3>
                <p className="text-muted mb-0">Pending</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Attestation Details by Attester</h5>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={() => exportReport('pdf')}>
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
              <button className="btn btn-outline-success" onClick={() => exportReport('xlsx')}>
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50', width: '30px' }}></th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Attester Name</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>User ID</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Email</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Campaigns</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Total Assigned</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Completed</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Pending</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Completion Rate</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {attesters.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        No attestation data available for the selected date range
                      </td>
                    </tr>
                  ) : (
                    attesters.map((attester, index) => (
                      <Fragment key={index}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => toggleRow(index)}>
                          <td>
                            <i className={`fas fa-chevron-${expandedRows.includes(index) ? 'down' : 'right'}`}></i>
                          </td>
                          <td>{attester.attester_email}</td>
                          <td>
                            <span className="badge bg-info me-1">{attester.campaign_count || 0}</span>
                            {attester.campaign || 'N/A'}
                          </td>
                          <td className="text-center">{attester.total_assigned || 0}</td>
                          <td className="text-center">{attester.completed || 0}</td>
                          <td className="text-center">{attester.pending || 0}</td>
                          <td className="text-center">
                            <span className={`badge ${
                              attester.completion_rate >= 90 ? 'bg-success' :
                              attester.completion_rate >= 70 ? 'bg-info' :
                              attester.completion_rate >= 50 ? 'bg-warning text-dark' :
                              'bg-danger'
                            }`}>
                              {Math.round(attester.completion_rate || 0)}%
                            </span>
                          </td>
                          <td>{attester.last_activity ? formatDateTime(attester.last_activity) : 'N/A'}</td>
                        </tr>
                        {expandedRows.includes(index) && attester.campaigns && (
                          <tr>
                            <td colSpan="8" style={{ backgroundColor: '#f8f9fa', padding: '15px' }}>
                              <div className="ms-4">
                                <h6 className="mb-3">Campaign Breakdown:</h6>
                                <div className="table-responsive">
                                  <table className="table table-sm table-bordered">
                                    <thead style={{ backgroundColor: '#e9ecef' }}>
                                      <tr>
                                        <th>Campaign Name</th>
                                        <th className="text-center">Assigned</th>
                                        <th className="text-center">Completed</th>
                                        <th className="text-center">Pending</th>
                                        <th className="text-center">Rate</th>
                                        <th>Last Activity</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {attester.campaigns.map((campaign, campIndex) => {
                                        const campRate = campaign.total_assigned > 0
                                          ? (campaign.completed / campaign.total_assigned * 100)
                                          : 0;
                                        return (
                                          <tr key={campIndex}>
                                            <td>{campaign.campaign_name}</td>
                                            <td className="text-center">{campaign.total_assigned}</td>
                                            <td className="text-center">{campaign.completed}</td>
                                            <td className="text-center">{campaign.pending}</td>
                                            <td className="text-center">
                                              <span className={`badge ${
                                                campRate >= 90 ? 'bg-success' :
                                                campRate >= 70 ? 'bg-info' :
                                                campRate >= 50 ? 'bg-warning text-dark' :
                                                'bg-danger'
                                              }`}>
                                                {Math.round(campRate)}%
                                              </span>
                                            </td>
                                            <td>{campaign.last_activity ? formatDateTime(campaign.last_activity) : 'N/A'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAttestationReport = () => {
    return <AttestationReportContent />;
  };

  const renderUserPrivilegesReport = () => {
    return (
      <div>
        {renderFilters()}

        {/* Additional Filters */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label small">Database Name</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search database..."
                  value={selectedDatabase}
                  onChange={(e) => setSelectedDatabase(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Username</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search username..."
                  value={selectedUsername}
                  onChange={(e) => setSelectedUsername(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Privilege Level</label>
                <select
                  className="form-select form-select-sm"
                  value={privilegeLevel}
                  onChange={(e) => setPrivilegeLevel(e.target.value)}
                >
                  <option value="all">All Levels</option>
                  <option value="4">Level 4 - SUPER_ADMIN</option>
                  <option value="3">Level 3 - ADMIN</option>
                  <option value="2">Level 2 - WRITE</option>
                  <option value="1">Level 1 - READ</option>
                  <option value="0">Level 0 - CONNECT</option>
                </select>
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <button className="btn btn-primary btn-sm w-100">
                  <i className="fas fa-search me-1"></i> Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-database fa-2x text-primary mb-2"></i>
                <h3 className="text-primary mb-2">1</h3>
                <p className="text-muted mb-0">Databases</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-users fa-2x text-info mb-2"></i>
                <h3 className="text-info mb-2">23</h3>
                <p className="text-muted mb-0">Total Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-exclamation-triangle fa-2x text-danger mb-2"></i>
                <h3 className="text-danger mb-2">16</h3>
                <p className="text-muted mb-0">Critical Privileges</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-key fa-2x text-warning mb-2"></i>
                <h3 className="text-warning mb-2">784</h3>
                <p className="text-muted mb-0">System Privileges</p>
              </div>
            </div>
          </div>
        </div>

        {/* Privilege Level Distribution */}
        <div className="row mb-4">
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white">
                <h5 className="mb-0">System Privilege Level Distribution</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-circle text-danger me-2"></i>Level 4 - SUPER_ADMIN</span>
                        <strong>5 privileges</strong>
                      </div>
                      <div className="progress" style={{ height: '12px' }}>
                        <div className="progress-bar bg-danger" style={{ width: '100%' }}></div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-circle text-warning me-2"></i>Level 3 - ADMIN</span>
                        <strong>756 privileges</strong>
                      </div>
                      <div className="progress" style={{ height: '12px' }}>
                        <div className="progress-bar bg-warning" style={{ width: '96%' }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-circle text-info me-2"></i>Level 2 - WRITE</span>
                        <strong>676 privileges</strong>
                      </div>
                      <div className="progress" style={{ height: '12px' }}>
                        <div className="progress-bar bg-info" style={{ width: '86%' }}></div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-circle text-success me-2"></i>Level 1 - READ</span>
                        <strong>76 privileges</strong>
                      </div>
                      <div className="progress" style={{ height: '12px' }}>
                        <div className="progress-bar bg-success" style={{ width: '10%' }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-circle text-secondary me-2"></i>Level 0 - CONNECT</span>
                        <strong>60 privileges</strong>
                      </div>
                      <div className="progress" style={{ height: '12px' }}>
                        <div className="progress-bar bg-secondary" style={{ width: '8%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Privileges Table */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">User Privilege Details</h5>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={() => exportReport('pdf')}>
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
              <button className="btn btn-outline-success" onClick={() => exportReport('xlsx')}>
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Username</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Database</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Account Status</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>System Privs</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Table Privs</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Roles</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Max Level</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Critical</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>SYS</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td className="text-center">705</td>
                    <td className="text-center">0</td>
                    <td className="text-center">0</td>
                    <td className="text-center"><span className="badge bg-danger">4 - SUPER_ADMIN</span></td>
                    <td className="text-center"><span className="badge bg-danger">Yes</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary">
                        <i className="fas fa-eye"></i> View
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>SYSTEM</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td className="text-center">45</td>
                    <td className="text-center">0</td>
                    <td className="text-center">0</td>
                    <td className="text-center"><span className="badge bg-warning">3 - ADMIN</span></td>
                    <td className="text-center"><span className="badge bg-warning">Yes</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary">
                        <i className="fas fa-eye"></i> View
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>UAR</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td className="text-center">2</td>
                    <td className="text-center">145</td>
                    <td className="text-center">3</td>
                    <td className="text-center"><span className="badge bg-secondary">0 - CONNECT</span></td>
                    <td className="text-center"><span className="badge bg-secondary">No</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary">
                        <i className="fas fa-eye"></i> View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper functions for table sorting, filtering, and pagination
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getFilteredAndSortedData = () => {
    if (!privilegeChangesData.recent_changes) return [];

    let filtered = [...privilegeChangesData.recent_changes];

    // Apply filters
    if (tableFilters.username) {
      filtered = filtered.filter(change =>
        change.username.toLowerCase().includes(tableFilters.username.toLowerCase())
      );
    }
    if (tableFilters.database) {
      filtered = filtered.filter(change =>
        change.database_name.toLowerCase().includes(tableFilters.database.toLowerCase())
      );
    }
    if (tableFilters.changeType !== 'all') {
      filtered = filtered.filter(change => change.change_type === tableFilters.changeType);
    }

    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        if (sortColumn === 'username') {
          aVal = a.username.toLowerCase();
          bVal = b.username.toLowerCase();
        } else if (sortColumn === 'database') {
          aVal = a.database_name.toLowerCase();
          bVal = b.database_name.toLowerCase();
        } else if (sortColumn === 'changeType') {
          aVal = a.change_type;
          bVal = b.change_type;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const getPaginatedData = () => {
    const filtered = getFilteredAndSortedData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filtered = getFilteredAndSortedData();
    return Math.ceil(filtered.length / rowsPerPage);
  };

  // Helper function to render privilege change badges based on database type
  const renderPrivilegeChangeBadges = (change) => {
    // Determine database type based on active report
    const dbType = activeReport === 'privilege-changes-mysql' ? 'mysql' : 'oracle';

    if (dbType === 'mysql') {
      // MySQL: Global, Database, Table, Roles
      return (
        <>
          {change.global_change !== undefined && change.global_change !== 0 && (
            <span className={`badge ${change.global_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Global: {change.global_change > 0 ? '+' : ''}{change.global_change}
            </span>
          )}
          {change.database_change !== undefined && change.database_change !== 0 && (
            <span className={`badge ${change.database_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              DB: {change.database_change > 0 ? '+' : ''}{change.database_change}
            </span>
          )}
          {change.table_change !== undefined && change.table_change !== 0 && (
            <span className={`badge ${change.table_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Table: {change.table_change > 0 ? '+' : ''}{change.table_change}
            </span>
          )}
          {change.role_change !== undefined && change.role_change !== 0 && (
            <span className={`badge ${change.role_change > 0 ? 'bg-success' : 'bg-danger'}`}>
              Roles: {change.role_change > 0 ? '+' : ''}{change.role_change}
            </span>
          )}
          {(change.global_change === 0 && change.database_change === 0 && change.table_change === 0 && change.role_change === 0) && (
            <span className="badge bg-secondary">No Change</span>
          )}
        </>
      );
    } else if (dbType === 'oracle') {
      // Oracle: System, Object, Roles
      return (
        <>
          {change.system_change !== undefined && change.system_change !== 0 && (
            <span className={`badge ${change.system_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Sys: {change.system_change > 0 ? '+' : ''}{change.system_change}
            </span>
          )}
          {change.object_change !== undefined && change.object_change !== 0 && (
            <span className={`badge ${change.object_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Obj: {change.object_change > 0 ? '+' : ''}{change.object_change}
            </span>
          )}
          {change.role_change !== undefined && change.role_change !== 0 && (
            <span className={`badge ${change.role_change > 0 ? 'bg-success' : 'bg-danger'}`}>
              Roles: {change.role_change > 0 ? '+' : ''}{change.role_change}
            </span>
          )}
          {(change.system_change === 0 && change.object_change === 0 && change.role_change === 0) && (
            <span className="badge bg-secondary">No Change</span>
          )}
        </>
      );
    } else {
      // 'all' - show database_type tag and appropriate fields based on what's available
      const isMySQLRecord = change.database_type === 'MySQL' || (change.global_change !== undefined);
      const isOracleRecord = change.database_type === 'Oracle' || (change.system_change !== undefined);

      return (
        <>
          {change.database_type && (
            <span className="badge bg-secondary me-1">{change.database_type}</span>
          )}
          {isMySQLRecord && change.global_change !== undefined && change.global_change !== 0 && (
            <span className={`badge ${change.global_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Global: {change.global_change > 0 ? '+' : ''}{change.global_change}
            </span>
          )}
          {isMySQLRecord && change.database_change !== undefined && change.database_change !== 0 && (
            <span className={`badge ${change.database_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              DB: {change.database_change > 0 ? '+' : ''}{change.database_change}
            </span>
          )}
          {isMySQLRecord && change.table_change !== undefined && change.table_change !== 0 && (
            <span className={`badge ${change.table_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Table: {change.table_change > 0 ? '+' : ''}{change.table_change}
            </span>
          )}
          {isOracleRecord && change.system_change !== undefined && change.system_change !== 0 && (
            <span className={`badge ${change.system_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Sys: {change.system_change > 0 ? '+' : ''}{change.system_change}
            </span>
          )}
          {isOracleRecord && change.object_change !== undefined && change.object_change !== 0 && (
            <span className={`badge ${change.object_change > 0 ? 'bg-success' : 'bg-danger'} me-1`}>
              Obj: {change.object_change > 0 ? '+' : ''}{change.object_change}
            </span>
          )}
          {change.role_change !== undefined && change.role_change !== 0 && (
            <span className={`badge ${change.role_change > 0 ? 'bg-success' : 'bg-danger'}`}>
              Roles: {change.role_change > 0 ? '+' : ''}{change.role_change}
            </span>
          )}
        </>
      );
    }
  };

  // Helper function to render current privileges based on database type
  const renderCurrentPrivileges = (change) => {
    const dbType = activeReport === 'privilege-changes-mysql' ? 'mysql' : 'oracle';

    if (dbType === 'mysql') {
      return (
        <small>
          Global: {change.latest_global_privileges || 0} |
          DB: {change.latest_database_privileges || 0} |
          Table: {change.latest_table_privileges || 0} |
          Roles: {change.latest_roles || 0}
        </small>
      );
    } else if (dbType === 'oracle') {
      return (
        <small>
          Sys: {change.latest_system_privileges || 0} |
          Obj: {change.latest_object_privileges || 0} |
          Roles: {change.latest_roles || 0}
        </small>
      );
    } else {
      // 'all' - show based on what fields are available
      const isMySQLRecord = change.database_type === 'MySQL' || (change.latest_global_privileges !== undefined);

      if (isMySQLRecord) {
        return (
          <small>
            Global: {change.latest_global_privileges || 0} |
            DB: {change.latest_database_privileges || 0} |
            Table: {change.latest_table_privileges || 0} |
            Roles: {change.latest_roles || 0}
          </small>
        );
      } else {
        return (
          <small>
            Sys: {change.latest_system_privileges || 0} |
            Obj: {change.latest_object_privileges || 0} |
            Roles: {change.latest_roles || 0}
          </small>
        );
      }
    }
  };

  // Helper function to render previous privileges based on database type
  const renderPreviousPrivileges = (change) => {
    const dbType = activeReport === 'privilege-changes-mysql' ? 'mysql' : 'oracle';

    if (dbType === 'mysql') {
      return (
        <small>
          Global: {change.previous_global_privileges || 0} |
          DB: {change.previous_database_privileges || 0} |
          Table: {change.previous_table_privileges || 0} |
          Roles: {change.previous_roles || 0}
        </small>
      );
    } else if (dbType === 'oracle') {
      return (
        <small>
          Sys: {change.previous_system_privileges || 0} |
          Obj: {change.previous_object_privileges || 0} |
          Roles: {change.previous_roles || 0}
        </small>
      );
    } else {
      // 'all' - show based on what fields are available
      const isMySQLRecord = change.database_type === 'MySQL' || (change.previous_global_privileges !== undefined);

      if (isMySQLRecord) {
        return (
          <small>
            Global: {change.previous_global_privileges || 0} |
            DB: {change.previous_database_privileges || 0} |
            Table: {change.previous_table_privileges || 0} |
            Roles: {change.previous_roles || 0}
          </small>
        );
      } else {
        return (
          <small>
            Sys: {change.previous_system_privileges || 0} |
            Obj: {change.previous_object_privileges || 0} |
            Roles: {change.previous_roles || 0}
          </small>
        );
      }
    }
  };

  const renderPrivilegeChangesReport = () => {
    const paginatedData = getPaginatedData();
    const totalPages = getTotalPages();
    const totalRecords = getFilteredAndSortedData().length;

    return (
      <div>

        {/* Date Range Filters */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small">From Date (Baseline)</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate}
                />
                <small className="text-muted">Collection date to use as baseline</small>
              </div>
              <div className="col-md-4">
                <label className="form-label small">To Date (Compare With)</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  min={fromDate}
                />
                <small className="text-muted">Collection date to compare against baseline</small>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button className="btn btn-primary btn-sm w-100" onClick={fetchPrivilegeChangesData}>
                  <i className="fas fa-sync-alt me-1"></i> Compare Collections
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Change Summary */}
        <div className="row mb-4">
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-plus-circle fa-2x text-success mb-2"></i>
                <h3 className="text-success mb-2">{privilegeChangesData.statistics.privileges_granted || 0}</h3>
                <p className="text-muted mb-0 small">Privileges Granted</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-minus-circle fa-2x text-danger mb-2"></i>
                <h3 className="text-danger mb-2">{privilegeChangesData.statistics.privileges_revoked || 0}</h3>
                <p className="text-muted mb-0 small">Privileges Revoked</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-user-plus fa-2x text-info mb-2"></i>
                <h3 className="text-info mb-2">{privilegeChangesData.statistics.new_users || 0}</h3>
                <p className="text-muted mb-0 small">New Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-user-minus fa-2x text-warning mb-2"></i>
                <h3 className="text-warning mb-2">{privilegeChangesData.statistics.removed_users || 0}</h3>
                <p className="text-muted mb-0 small">Removed Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-database fa-2x text-primary mb-2"></i>
                <h3 className="text-primary mb-2">{privilegeChangesData.statistics.total_collections || 0}</h3>
                <p className="text-muted mb-0 small">Collections</p>
              </div>
            </div>
          </div>
          <div className="col-md-4 col-lg-2">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-chart-line fa-2x text-secondary mb-2"></i>
                <h3 className="text-secondary mb-2">{privilegeChangesData.statistics.net_change || 0}</h3>
                <p className="text-muted mb-0 small">Net Change</p>
              </div>
            </div>
          </div>
        </div>

        {/* Change History Table */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Privilege Change History
              {totalRecords > 0 && <small className="text-muted ms-2">({totalRecords} record{totalRecords !== 1 ? 's' : ''})</small>}
            </h5>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={() => exportReport('pdf')}>
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
              <button className="btn btn-outline-success" onClick={() => exportReport('xlsx')}>
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
            </div>
          </div>
          <div className="card-body">
            {/* Filter Row */}
            <div className="row mb-3 g-2">
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Filter by Username..."
                  value={tableFilters.username}
                  onChange={(e) => {
                    setTableFilters({ ...tableFilters, username: e.target.value });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="col-md-3">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Filter by Database..."
                  value={tableFilters.database}
                  onChange={(e) => {
                    setTableFilters({ ...tableFilters, database: e.target.value });
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="col-md-3">
                <select
                  className="form-select form-select-sm"
                  value={tableFilters.changeType}
                  onChange={(e) => {
                    setTableFilters({ ...tableFilters, changeType: e.target.value });
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Change Types</option>
                  <option value="NEW_USER">New User</option>
                  <option value="GRANTED">Granted</option>
                  <option value="REVOKED">Revoked</option>
                  <option value="MODIFIED">Modified</option>
                </select>
              </div>
              <div className="col-md-3 text-end">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setTableFilters({ username: '', database: '', changeType: 'all' });
                    setSortColumn('');
                    setCurrentPage(1);
                  }}
                >
                  <i className="fas fa-redo me-1"></i> Reset Filters
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Date</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Change Date</th>
                    <th
                      style={{ fontWeight: '700', color: '#2c3e50', cursor: 'pointer' }}
                      onClick={() => handleSort('username')}
                    >
                      Username
                      {sortColumn === 'username' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th
                      style={{ fontWeight: '700', color: '#2c3e50', cursor: 'pointer' }}
                      onClick={() => handleSort('database')}
                    >
                      Database
                      {sortColumn === 'database' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th
                      style={{ fontWeight: '700', color: '#2c3e50', cursor: 'pointer' }}
                      onClick={() => handleSort('changeType')}
                    >
                      Change Type
                      {sortColumn === 'changeType' && (
                        <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Changes</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Current Privileges</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Previous Privileges</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData && paginatedData.length > 0 ? (
                    paginatedData.map((change, index) => (
                      <tr key={index}>
                        <td>{change.latest_collection ? change.latest_collection.split(' ')[0] : 'N/A'}</td>
                        <td>{change.change_date || 'N/A'}</td>
                        <td><strong>{change.username}</strong></td>
                        <td>{change.database_name}</td>
                        <td>
                          {change.change_type === 'NEW_USER' && <span className="badge bg-info">New User</span>}
                          {change.change_type === 'GRANTED' && <span className="badge bg-success">Granted</span>}
                          {change.change_type === 'REVOKED' && <span className="badge bg-danger">Revoked</span>}
                          {change.change_type === 'MODIFIED' && <span className="badge bg-warning">Modified</span>}
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => {
                              setSelectedChange(change);
                              setShowChangeModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {renderPrivilegeChangeBadges(change)}
                          </button>
                        </td>
                        <td>
                          {renderCurrentPrivileges(change)}
                        </td>
                        <td>
                          {renderPreviousPrivileges(change)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-3">
                        {loading ? 'Loading changes...' : 'No privilege changes detected in the selected time period'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalRecords)} of {totalRecords} records
                </div>
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => {
                        const pageNum = i + 1;
                        // Show first page, last page, current page, and 2 pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                        ) {
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                          return (
                            <li key={pageNum} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        return null;
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Change Details Modal */}
        {showChangeModal && selectedChange && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Privilege Change Details - {selectedChange.username}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowChangeModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Database:</strong> {selectedChange.database_name}
                    </div>
                    <div className="col-md-6">
                      <strong>Change Date:</strong> {selectedChange.change_date}
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Collection Date:</strong> {selectedChange.latest_collection ? selectedChange.latest_collection.split(' ')[0] : 'N/A'}
                    </div>
                    <div className="col-md-6">
                      <strong>Change Type:</strong>{' '}
                      {selectedChange.change_type === 'NEW_USER' && <span className="badge bg-info">New User</span>}
                      {selectedChange.change_type === 'GRANTED' && <span className="badge bg-success">Granted</span>}
                      {selectedChange.change_type === 'REVOKED' && <span className="badge bg-danger">Revoked</span>}
                      {selectedChange.change_type === 'MODIFIED' && <span className="badge bg-warning">Modified</span>}
                    </div>
                  </div>

                  <h6 className="mt-4 mb-3">Change Summary</h6>
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th>Type</th>
                        <th className="text-center">Previous</th>
                        <th className="text-center">Current</th>
                        <th className="text-center">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>System Privileges</strong></td>
                        <td className="text-center">{selectedChange.previous_system_privileges}</td>
                        <td className="text-center">{selectedChange.latest_system_privileges}</td>
                        <td className="text-center">
                          {selectedChange.system_change !== undefined && selectedChange.system_change !== 0 ? (
                            <span className={`badge ${selectedChange.system_change > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {selectedChange.system_change > 0 ? '+' : ''}{selectedChange.system_change}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">0</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Object Privileges</strong></td>
                        <td className="text-center">{selectedChange.previous_object_privileges}</td>
                        <td className="text-center">{selectedChange.latest_object_privileges}</td>
                        <td className="text-center">
                          {selectedChange.object_change !== undefined && selectedChange.object_change !== 0 ? (
                            <span className={`badge ${selectedChange.object_change > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {selectedChange.object_change > 0 ? '+' : ''}{selectedChange.object_change}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">0</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Roles</strong></td>
                        <td className="text-center">{selectedChange.previous_roles}</td>
                        <td className="text-center">{selectedChange.latest_roles}</td>
                        <td className="text-center">
                          {selectedChange.role_change !== undefined && selectedChange.role_change !== 0 ? (
                            <span className={`badge ${selectedChange.role_change > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {selectedChange.role_change > 0 ? '+' : ''}{selectedChange.role_change}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">0</span>
                          )}
                        </td>
                      </tr>
                      <tr className="table-light">
                        <td><strong>Total</strong></td>
                        <td className="text-center">
                          <strong>
                            {selectedChange.previous_system_privileges +
                             selectedChange.previous_object_privileges +
                             selectedChange.previous_roles}
                          </strong>
                        </td>
                        <td className="text-center">
                          <strong>
                            {selectedChange.latest_system_privileges +
                             selectedChange.latest_object_privileges +
                             selectedChange.latest_roles}
                          </strong>
                        </td>
                        <td className="text-center">
                          {selectedChange.total_change !== undefined && selectedChange.total_change !== 0 ? (
                            <span className={`badge ${selectedChange.total_change > 0 ? 'bg-success' : 'bg-danger'}`}>
                              {selectedChange.total_change > 0 ? '+' : ''}{selectedChange.total_change}
                            </span>
                          ) : (
                            <span className="badge bg-warning">0 (Modified)</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowChangeModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPrivilegeAuditReport = () => {
    return (
      <div>
        {renderFilters()}

        {/* Critical Findings Summary */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-user-shield fa-2x text-danger mb-2"></i>
                <h3 className="text-danger mb-2">5</h3>
                <p className="text-muted mb-0">SYSDBA Accounts</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-exclamation-triangle fa-2x text-warning mb-2"></i>
                <h3 className="text-warning mb-2">16</h3>
                <p className="text-muted mb-0">Over-Privileged Users</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-user-times fa-2x text-info mb-2"></i>
                <h3 className="text-info mb-2">12</h3>
                <p className="text-muted mb-0">Inactive with Privs</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center">
                <i className="fas fa-key fa-2x text-secondary mb-2"></i>
                <h3 className="text-secondary mb-2">756</h3>
                <p className="text-muted mb-0">Critical Privileges</p>
              </div>
            </div>
          </div>
        </div>

        {/* SYSDBA/SYSOPER Users */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <h5 className="mb-0 text-danger">
              <i className="fas fa-exclamation-circle me-2"></i>
              SUPER_ADMIN Privilege Holders (SYSDBA, SYSOPER)
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Username</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Database</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Account Status</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Privilege</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Admin Option</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Last Login</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Risk Level</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>SYS</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td><span className="badge bg-danger">SYSDBA</span></td>
                    <td><span className="badge bg-danger">YES</span></td>
                    <td>2025-10-18 00:53</td>
                    <td><span className="badge bg-danger">CRITICAL</span></td>
                  </tr>
                  <tr>
                    <td><strong>SYSTEM</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td><span className="badge bg-danger">SYSOPER</span></td>
                    <td><span className="badge bg-danger">YES</span></td>
                    <td>2025-10-17 23:56</td>
                    <td><span className="badge bg-danger">CRITICAL</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Over-Privileged Users */}
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-header bg-white">
            <h5 className="mb-0 text-warning">
              <i className="fas fa-user-lock me-2"></i>
              Over-Privileged Users (ADMIN Level)
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Username</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Database</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Admin Privs</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Critical Privileges</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Account Status</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Last Login</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>DBA</strong></td>
                    <td>XE</td>
                    <td className="text-center">705</td>
                    <td><small>ALTER ANY TABLE, DROP ANY TABLE, GRANT ANY...</small></td>
                    <td><span className="badge bg-success">OPEN</span></td>
                    <td>Never</td>
                    <td><small className="text-warning">Review necessity of privileges</small></td>
                  </tr>
                  <tr>
                    <td><strong>IMP_FULL_DATABASE</strong></td>
                    <td>XE</td>
                    <td className="text-center">243</td>
                    <td><small>CREATE ANY TABLE, INSERT ANY TABLE...</small></td>
                    <td><span className="badge bg-warning text-dark">LOCKED</span></td>
                    <td>2025-09-20</td>
                    <td><small className="text-info">Consider revoking if unused</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Inactive Users with Privileges */}
        <div className="card shadow-sm border-0">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0 text-info">
              <i className="fas fa-user-clock me-2"></i>
              Inactive Users with Privileges
            </h5>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-primary" onClick={() => exportReport('pdf')}>
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
              <button className="btn btn-outline-success" onClick={() => exportReport('xlsx')}>
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead style={{ backgroundColor: '#f8f9fc' }}>
                  <tr>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Username</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Database</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Account Status</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Total Privs</th>
                    <th className="text-center" style={{ fontWeight: '700', color: '#2c3e50' }}>Admin Privs</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Last Login</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Days Inactive</th>
                    <th style={{ fontWeight: '700', color: '#2c3e50' }}>Recommendation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>ANONYMOUS</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-danger">LOCKED</span></td>
                    <td className="text-center">45</td>
                    <td className="text-center">12</td>
                    <td>Never</td>
                    <td><span className="badge bg-danger">∞</span></td>
                    <td><small className="text-danger">Revoke all privileges</small></td>
                  </tr>
                  <tr>
                    <td><strong>OUTLN</strong></td>
                    <td>XE</td>
                    <td><span className="badge bg-warning text-dark">EXPIRED & LOCKED</span></td>
                    <td className="text-center">23</td>
                    <td className="text-center">5</td>
                    <td>2025-03-15</td>
                    <td><span className="badge bg-warning">217</span></td>
                    <td><small className="text-warning">Review and remove if not needed</small></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            {activeReport === 'dashboard' && 'Reports Dashboard'}
            {activeReport === 'attestation' && 'Attestation Status Report'}
            {activeReport === 'user-privileges' && 'User Privileges Report'}
            {activeReport === 'privilege-changes-oracle' && 'Privilege Changes Over Time - Oracle'}
            {activeReport === 'privilege-changes-mysql' && 'Privilege Changes Over Time - MySQL'}
            {activeReport === 'privilege-audit' && 'Privilege Auditing Report'}
          </h2>
          <p className="text-muted mb-0">
            {activeReport === 'dashboard' && 'Comprehensive reporting and analytics for UAR campaigns'}
            {activeReport === 'attestation' && 'Real-time tracking of attestation activities and completion'}
            {activeReport === 'user-privileges' && 'Oracle database privileges by user, role, and framework with risk classification'}
            {activeReport === 'privilege-changes-oracle' && 'Historical analysis of Oracle privilege grants, revocations, and escalations'}
            {activeReport === 'privilege-changes-mysql' && 'Historical analysis of MySQL privilege grants, revocations, and escalations'}
            {activeReport === 'privilege-audit' && 'Critical privilege monitoring, over-privileged user detection, and compliance auditing'}
          </p>
        </div>
        {activeReport !== 'dashboard' && (
          <button
            className="btn btn-outline-secondary"
            onClick={() => setActiveReport('dashboard')}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Dashboard
          </button>
        )}
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3">Loading report data...</p>
        </div>
      )}

      {/* Report Content */}
      {!loading && (
        <>
          {activeReport === 'dashboard' && renderDashboard()}
          {activeReport === 'attestation' && renderAttestationReport()}
          {activeReport === 'user-privileges' && renderUserPrivilegesReport()}
          {activeReport === 'privilege-changes-oracle' && renderPrivilegeChangesReport()}
          {activeReport === 'privilege-changes-mysql' && renderPrivilegeChangesReport()}
          {activeReport === 'privilege-audit' && renderPrivilegeAuditReport()}
        </>
      )}
    </div>
  );
};

export default Reports;
